import React, { useEffect, useState } from "react";
import { Apple, Loader2, ShoppingCart } from "lucide-react";
import { api } from "@/lib/wolfApi";
import { toast } from "sonner";

const PROMO_IMG = "https://static.prod-images.emergentagent.com/jobs/8d6d1392-6fb1-4225-9c6b-7e3e7b017e22/images/2e8c6b8dcbf8d46dc93b088d53003f2029176ff0e54ac163782184e87056eca6.jpeg";

const DAWG_FEATURES = [
  "Multitrack Recording", "Pro Mixing Desk", "FX Racks", "Beat Sequencer",
  "Live Looping", "Sampler", "Vocal Tuning", "Stem Export", "Gold-Standard Mastering",
];

export const WolfDawgSection = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyEmail, setBuyEmail] = useState("");
  const [buying, setBuying] = useState(false);
  const [purchased, setPurchased] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("dawg_session_id");
    if (!sessionId) return;
    window.history.replaceState({}, "", window.location.pathname);
    const poll = async (attempt) => {
      if (attempt > 8) return;
      try {
        const r = await api.get(`/merch/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPurchased(true);
          toast.success("WOLF DAWG is yours 🐺🔴 Check your inbox.");
          return;
        }
        if (r.data.status === "expired") return;
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll(0);
  }, []);

  const buyNow = async (e) => {
    e.preventDefault();
    setBuying(true);
    try {
      const r = await api.post("/dawg/checkout", { email: buyEmail, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch {
      toast.error("Checkout failed — try again.");
      setBuying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/dawg/notify", { email });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="relative py-24 sm:py-36 px-6 sm:px-10 lg:px-16 overflow-hidden border-y border-[#FF2B2B]/20 bg-[#0a0a0a]" data-testid="wolf-dawg-section">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 70% 50% at 70% 30%, rgba(255,43,43,0.07), transparent 60%)" }} />

      {/* FEATURE TEASER MARQUEE */}
      <div aria-hidden className="absolute top-0 left-0 right-0 overflow-hidden border-b border-[#FF2B2B]/15 py-3 bg-black/60" data-testid="dawg-feature-marquee">
        <div className="marquee-track whitespace-nowrap flex gap-10 text-[11px] uppercase tracking-[0.3em] text-white/35">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-10 shrink-0">
              {DAWG_FEATURES.map((f) => (
                <React.Fragment key={f}>
                  <span>{f}</span>
                  <span className="text-[#FF2B2B]">◆</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="relative max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <div className="flex items-center gap-4 flex-wrap">            <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37]">— Wolf Music Presents</p>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF2B2B] border border-[#FF2B2B]/50 px-3 py-1.5 animate-pulse" data-testid="dawg-coming-soon-badge">
              Coming Soon
            </span>
          </div>

          <h2 className="mt-6 font-display leading-none">
            <span className="wolf-gold-foil block text-5xl sm:text-7xl">WOLF DAWG</span>
            <span className="block text-xl sm:text-3xl tracking-[0.25em] text-[#FF2B2B] mt-3">UNLEASH YOUR SOUND</span>
          </h2>

          <p className="mt-6 font-editorial italic text-lg text-white/55 max-w-xl">
            The new digital audio workstation forged by WOLF Music. Record, mix and unleash —
            built for the pack, by the pack. Rockstar power in your hands.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4" data-testid="dawg-pricing">
            <div className="flex-1 border border-white/15 bg-black/60 p-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">App Store</p>
              <p className="font-display text-3xl text-white mt-2">$33 <span className="text-sm text-white/40">AUD</span></p>
            </div>
            <div className="flex-1 border border-[#D4AF37]/60 bg-black/60 p-5 relative">
              <span className="absolute -top-2.5 right-4 text-[9px] uppercase tracking-[0.25em] text-black bg-[#FF2B2B] px-2 py-0.5 font-bold">Save 10%</span>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">Direct from WOLF</p>
              <p className="font-display text-3xl text-[#D4AF37] mt-2">
                $29.70 <span className="text-sm text-[#D4AF37]/50">AUD</span>
                <span className="ml-3 text-base text-white/30 line-through">$33</span>
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3">
            {purchased ? (
              <p className="border border-[#D4AF37]/60 bg-black/60 p-5 text-sm text-[#D4AF37]" data-testid="dawg-purchase-success">
                WOLF DAWG is yours, locked at $29.70. 🐺🔴 Purchase confirmation sent — your download link lands in your inbox the moment the build drops.
              </p>
            ) : buyOpen ? (
              <form onSubmit={buyNow} className="flex flex-col sm:flex-row gap-3" data-testid="dawg-buy-form">
                <input
                  type="email"
                  required
                  value={buyEmail}
                  onChange={(e) => setBuyEmail(e.target.value)}
                  placeholder="Email for your receipt + download…"
                  data-testid="dawg-buy-email-input"
                  className="flex-1 bg-black border border-[#D4AF37]/40 focus:border-[#D4AF37] outline-none text-white px-4 py-3.5 text-sm transition-colors"
                />
                <button
                  type="submit"
                  disabled={buying}
                  data-testid="dawg-buy-continue-btn"
                  className="inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.28em] text-black bg-[#D4AF37] hover:bg-[#e6c457] disabled:opacity-60 px-7 py-3.5 font-bold transition-colors"
                >
                  {buying ? <Loader2 size={14} className="animate-spin" /> : "Pay $29.70"}
                </button>
              </form>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setBuyOpen(true)}
                  data-testid="dawg-buy-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2.5 text-[11px] uppercase tracking-[0.28em] px-6 py-4 font-bold transition-colors text-black bg-[#D4AF37] hover:bg-[#e6c457]"
                >
                  <ShoppingCart size={15} /> Buy Now — $29.70
                </button>
                <button
                  disabled
                  data-testid="dawg-appstore-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2.5 text-[11px] uppercase tracking-[0.28em] px-6 py-4 font-bold border border-white/20 text-white/35 cursor-not-allowed"
                >
                  <Apple size={15} /> App Store — Soon
                </button>
              </div>
            )}
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">
              Buy today, locked at launch price — download emailed the moment the build is released
            </p>
          </div>

          <div className="mt-8">
            {status === "done" ? (
              <p className="text-sm text-[#D4AF37]" data-testid="dawg-notify-success">
                You're on the launch list. First access when WOLF DAWG drops. 🐺
              </p>
            ) : (
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="dawg-notify-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for launch notification…"
                  data-testid="dawg-notify-email-input"
                  className="flex-1 bg-black border border-white/20 focus:border-[#FF2B2B] outline-none text-white px-4 py-3.5 text-sm transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="dawg-notify-submit-btn"
                  className="inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.28em] text-black bg-[#FF2B2B] hover:bg-[#ff4747] disabled:opacity-60 px-7 py-3.5 font-bold transition-colors"
                >
                  {status === "sending" ? <Loader2 size={14} className="animate-spin" /> : "Notify Me"}
                </button>
              </form>
            )}
            {status === "error" && (
              <p className="mt-2 text-xs text-red-400/90" data-testid="dawg-notify-error">Something went wrong — try again.</p>
            )}
          </div>
        </div>

        <div className="relative">
          <img
            src={PROMO_IMG}
            alt="WOLF DAWG — digital audio workstation"
            loading="lazy"
            className="w-full border border-[#FF2B2B]/30 shadow-[0_0_80px_rgba(255,43,43,0.12)]"
            data-testid="dawg-promo-image"
          />
          <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-white/30 text-center">
            In development · WOLF Music · Est. 1987
          </p>
        </div>
      </div>
    </section>
  );
};
